export type DraftMode = 'plain' | 'ghost' | 'coded';

export type DraftStatus = 'draft' | 'ready_to_send' | 'migrated' | 'sent';

export interface DraftRecipient {
  name?: string;
  email?: string;
  phone?: string;
  emailHint?: string;
  phoneHint?: string;
}

export interface MessageDraft {
  id: string;
  createdAt: string;
  updatedAt: string;
  body: string;
  mode: DraftMode;
  encodeKey?: string;
  recipient?: DraftRecipient;
  status: DraftStatus;
  cloudConversationId?: string;
}

export interface LocalContact {
  id: string;
  name: string;
  emails: string[];
  phones: string[];
  createdAt: string;
}

export interface AppLocalStore {
  version: 1;
  drafts: MessageDraft[];
  localContacts: LocalContact[];
  migration?: {
    completedAt?: string;
    clerkUserId?: string;
  };
}

const STORE_KEY = 'app:v1';

function emptyStore(): AppLocalStore {
  return { version: 1, drafts: [], localContacts: [] };
}

export function loadStore(): AppLocalStore {
  if (typeof window === 'undefined') return emptyStore();
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw) as AppLocalStore;
    if (parsed.version !== 1 || !Array.isArray(parsed.drafts)) return emptyStore();
    return {
      version: 1,
      drafts: parsed.drafts,
      localContacts: parsed.localContacts ?? [],
      migration: parsed.migration,
    };
  } catch {
    return emptyStore();
  }
}

export function saveStore(store: AppLocalStore): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORE_KEY, JSON.stringify(store));
}

export function maskEmail(email: string): string {
  const trimmed = email.trim().toLowerCase();
  const at = trimmed.indexOf('@');
  if (at <= 0) return '***';
  const local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1);
  const first = local.charAt(0);
  return `${first}***@${domain}`;
}

export function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return '***';
  return `***${digits.slice(-4)}`;
}

export function upsertDraft(draft: MessageDraft): MessageDraft {
  const store = loadStore();
  const idx = store.drafts.findIndex((d) => d.id === draft.id);
  const next = { ...draft, updatedAt: new Date().toISOString() };
  if (idx >= 0) {
    store.drafts[idx] = next;
  } else {
    store.drafts.unshift(next);
  }
  saveStore(store);
  return next;
}

export function deleteDraft(id: string): void {
  const store = loadStore();
  store.drafts = store.drafts.filter((d) => d.id !== id);
  saveStore(store);
}

export function getDraft(id: string): MessageDraft | undefined {
  return loadStore().drafts.find((d) => d.id === id);
}

export function listDrafts(): MessageDraft[] {
  return loadStore().drafts.sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

export function listPendingMigrationDrafts(): MessageDraft[] {
  return listDrafts().filter(
    (d) => d.status === 'ready_to_send' || d.status === 'draft'
  );
}

export function upsertLocalContact(contact: Omit<LocalContact, 'createdAt'> & { createdAt?: string }): LocalContact {
  const store = loadStore();
  const existing = store.localContacts.find((c) => c.id === contact.id);
  const next: LocalContact = {
    ...contact,
    createdAt: existing?.createdAt ?? contact.createdAt ?? new Date().toISOString(),
  };
  if (existing) {
    store.localContacts = store.localContacts.map((c) => (c.id === contact.id ? next : c));
  } else {
    store.localContacts.unshift(next);
  }
  saveStore(store);
  return next;
}

export function listLocalContacts(): LocalContact[] {
  return loadStore().localContacts;
}

export function markMigrationComplete(clerkUserId: string): void {
  const store = loadStore();
  store.migration = {
    completedAt: new Date().toISOString(),
    clerkUserId,
  };
  saveStore(store);
}

export function shouldRunMigration(clerkUserId: string): boolean {
  const store = loadStore();
  if (store.migration?.clerkUserId === clerkUserId && store.migration.completedAt) {
    const pending = listPendingMigrationDrafts().filter((d) => d.status !== 'sent');
    return pending.length > 0;
  }
  return listPendingMigrationDrafts().some((d) => d.status === 'ready_to_send' || d.status === 'draft');
}

export const JOIN_HINTS_KEY = 'app:joinHints';

export interface JoinHints {
  emailHint?: string;
  phoneHint?: string;
  ref?: string;
}

export function saveJoinHints(hints: JoinHints): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(JOIN_HINTS_KEY, JSON.stringify(hints));
}

export function loadJoinHints(): JoinHints | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(JOIN_HINTS_KEY);
    return raw ? (JSON.parse(raw) as JoinHints) : null;
  } catch {
    return null;
  }
}

export function clearJoinHints(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(JOIN_HINTS_KEY);
}

/** Pending referral code from /r/{code} — claimed once after new signup. */
export const PENDING_REFERRAL_CODE_KEY = 'app:pendingReferralCode';

export function savePendingReferralCode(code: string): void {
  if (typeof window === 'undefined') return;
  const normalized = code.trim().toUpperCase();
  if (!normalized) return;
  sessionStorage.setItem(PENDING_REFERRAL_CODE_KEY, normalized);
}

export function loadPendingReferralCode(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(PENDING_REFERRAL_CODE_KEY);
  return raw?.trim() ? raw.trim().toUpperCase() : null;
}

export function clearPendingReferralCode(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(PENDING_REFERRAL_CODE_KEY);
}
